import { VariableType } from 'json-to-graphql-query';

import { PublicSettings } from '@/enums/publicSettings.enum';
import { TableName } from '@/enums/tableName';
import { DocumentIndex } from '@/interfaces/documentIndex';
import { ListResponse } from '@/interfaces/list';
import store from '@/store/store';
import { config } from '@/utils/config';
import { EntityResource } from './entity.resource';

/**
 * Resource dokumentów z indeksu
 *
 * @export
 * @class DocumentIndexResource
 * @extends {EntityResource<DocumentIndex>}
 */
export class DocumentIndexResource extends EntityResource<DocumentIndex> {
  public getFields = {
    creatorId: true,
    documentId: true,
    stageId: true,
    statusId: true,
    values: true,
    parentId: true,
    parentProcedureId: true,
    procedureId: true,
    previousStatuses: true,
    data: true,
    decisionSecondYearTotal: true,
    decisions: {
      id: true,
      positive: true,
      startDate: true,
      endDate: true,
      data: true,
      type: true,
      dateRanges: true
    },
    signedAttachmentId: true,
    systemNumbers: true,
    currentDecision: {
      id: true,
      name: true,
      number: true,
      data: true,
      positive: true,
      documentId: true,
      type: true,
      startDate: true,
      endDate: true,
      dateRanges: true,
      dataDateRanges: true
    },
    procedure: {
      id: true,
      code: true
    },
    structureNode: {
      id: true,
      epuap: true
    },
    structureNodeId: true
  };

  public listFields = {
    needFix: true,
    created: true,
    documentId: true,
    procedureId: true,
    procedure: {
      id: true,
      templateId: true,
      name: true
    },
    data: true,
    statusId: true,
    status: { id: true, name: true, color: true },
    module: true,
    checksum: true,
    currentDecision: {
      id: true,
      name: true,
      number: true,
      data: true,
      positive: true,
      documentId: true,
      type: true,
      startDate: true,
      endDate: true,
      dateRanges: true,
      dataDateRanges: true
    },
    attachments: true,
    values: true,
    signedAttachmentId: true
  };

  public listVariables = { filters: 'MongoDBFilters' };

  constructor() {
    super({
      entityName: 'documentIndex'
    });
  }

  /**
   * Pobiera listę dokumentów
   *
   * @param {*} filters
   * @param {boolean} [preventLoader]
   * @returns {Promise<ListResponse<DocumentIndex>>}
   * @memberof DocumentIndexResource
   */
  public list(filters: any, preventLoader?: boolean): Promise<ListResponse<DocumentIndex>> {
    return super.list(filters, preventLoader).then(response =>
      Promise.all([
        store.dispatch(
          'auth/checkPermissionsSync',
          response.list.map(document => this.colorPermission(document))
        ),
        store
          .dispatch('setting/findPublic', {
            code: PublicSettings.IGNORE_CRITERIONS_FOR_COLOR,
            entityId: filters.procedureId?.toString(),
            entityType: TableName.procedure
          })
          .then(setting => {
            if (!setting?.value) {
              return [];
            }
            return JSON.parse(setting.value) as number[];
          })
      ]).then(([permissions, ignoreCriterions]) => {
        response.list.forEach(document => {
          const permission = this.colorPermission(document);
          if (!permissions[permission]) {
            return;
          }

          if (document.values && Object.keys(document.values).filter(key => key.startsWith('CRITERION_')).length) {
            const assessmentValues = Object.keys(document.values)
              .filter(key => key.startsWith('CRITERION_'))
              .filter(key => !ignoreCriterions.some(criterionId => key.endsWith(criterionId.toString())))
              .map(key => document.values[key])
              .filter(Boolean);
            const undefinedOptions = [undefined, null];
            if (
              config.procedure === 'dofinansowanie' &&
              assessmentValues.some(value => undefinedOptions.includes(value.valueFromAssessment))
            ) {
              // Do not set any list classess (OP#2410)!
            } else if (assessmentValues.some(value => value.passed === 4)) {
              document.listClasses = 'table-danger';
            } else if (assessmentValues.some(value => value.passed === 3)) {
              document.listClasses = 'table-warning';
            } else {
              document.listClasses = 'table-success';
            }
          } else if (document.data?.warnings?.length) {
            document.listClasses = 'table-warning';
          } else if (document.needFix) {
            document.listClasses = 'table-warning';
          }
        });
        return response;
      })
    );
  }

  /**
   * Pobiera listę dokumentów bez kolorowania
   *
   * @param {*} filters
   * @param {boolean} [preventLoader]
   * @returns {Promise<ListResponse<DocumentIndex>>}
   * @memberof DocumentIndexResource
   */
  public listNoColors(filters: any, preventLoader?: boolean): Promise<ListResponse<DocumentIndex>> {
    return super.list(filters, preventLoader);
  }

  /**
   * Pobiera statystyki
   *
   * @param {*} options
   * @returns {Promise<any>}
   * @memberof DocumentIndexResource
   */
  public statistics(options: any): Promise<any> {
    const query = this.prepareOptions({
      query: {
        __variables: { options: 'StatisticOptions!' },
        documentIndexStatistics: {
          __args: {
            options: new VariableType('options')
          }
        }
      }
    });
    return this.query({ query, variables: { options } }, 'documentIndexStatistics');
  }

  /**
   * Pobiera raport finansowy
   *
   * @param {*} variables
   * @returns {Promise<any>}
   * @memberof DocumentIndexResource
   */
  public financialReport(variables: any): Promise<any> {
    const query = this.prepareOptions({
      query: {
        __variables: { procedureId: 'Int!', structureIds: '[Int!]', filters: 'MongoDBFilters' },
        documentIndexFinancialReport: {
          __args: {
            procedureId: new VariableType('procedureId'),
            structureIds: new VariableType('structureIds'),
            filters: new VariableType('filters')
          },
          currentDecision: {
            id: true,
            data: true,
            dataDateRanges: true,
            dateRanges: true
          },
          data: true,
          decisionFirstYearTotal: true,
          decisionSecondYearTotal: true,
          decisionTotal: true,
          documentId: true,
          invoicesTotal: true,
          payments: true,
          procedureId: true,
          totalPaid: true,
          values: true
        }
      }
    });
    return this.query({ query, variables }, 'documentIndexFinancialReport');
  }

  /**
   * Zwraca uprawnienia wymagane do pokolorowania dokumentu
   *
   * @private
   * @param {DocumentIndex} document
   * @returns {string}
   * @memberof DocumentIndexResource
   */
  private colorPermission(document: DocumentIndex): string {
    return document.data?.school?.nodeId
      ? 'DOCUMENT.UPDATE:structure_node:' + document.data.school.nodeId
      : 'DOCUMENT.UPDATE';
  }
}

export const documentIndexResource = new DocumentIndexResource();
